import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Tag, Plus, Trash2, ToggleLeft, ToggleRight, RefreshCw, Loader2,
  ChevronDown, ChevronRight, ExternalLink, User,
} from 'lucide-react';
import { getCoupons, createCoupon, toggleCoupon, deleteCoupon, getCouponAnalytics } from '../services/adminService';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const fmtMoney = (n) => `৳${Number(n).toLocaleString('en-BD', { minimumFractionDigits: 0 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const EMPTY_FORM = {
  code: '',
  discountType: 0,          // 0=Percent, 1=Flat
  discountValue: '',
  minBookingAmount: '',
  maxUsageCount: '',
  expiryDate: '',
};

const fmtDiscount = (n) => `৳${Number(n).toLocaleString('en-BD', { minimumFractionDigits: 0 })}`;

// ── Usage Overview components ────────────────────────────────────────────────
function CouponPeriodCard({ periodData, onViewBooking }) {
  const [open, setOpen] = useState(false);
  const [expandedCode, setExpandedCode] = useState(null);

  return (
    <div className="border border-slate-700 rounded-lg overflow-hidden">
      {/* Period header */}
      <button
        onClick={() => setOpen(p => !p)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-800/60 hover:bg-slate-700/40 transition-colors"
      >
        <div className="flex items-center gap-3">
          {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
          <span className="font-semibold text-white">{periodData.period}</span>
          <span className="text-slate-400 text-sm">{periodData.totalUsages} uses</span>
        </div>
        <span className="text-green-400 font-semibold">{fmtDiscount(periodData.totalDiscount)} discount given</span>
      </button>

      {open && (
        <div className="divide-y divide-slate-700/50">
          {periodData.coupons.map(coupon => (
            <div key={coupon.code} className="bg-slate-800/20">
              {/* Coupon row */}
              <button
                onClick={() => setExpandedCode(expandedCode === coupon.code ? null : coupon.code)}
                className="w-full flex items-center justify-between px-6 py-2.5 hover:bg-slate-700/20 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {expandedCode === coupon.code
                    ? <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
                    : <ChevronRight className="h-3.5 w-3.5 text-slate-500" />}
                  <span className="font-mono font-bold text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded text-sm">
                    {coupon.code}
                  </span>
                  <span className="text-slate-400 text-xs">{coupon.usageCount} booking{coupon.usageCount !== 1 ? 's' : ''}</span>
                </div>
                <span className="text-amber-400 text-sm font-medium">{fmtDiscount(coupon.totalDiscount)}</span>
              </button>

              {/* Booking rows */}
              {expandedCode === coupon.code && (
                <div className="mx-6 mb-3 rounded-lg border border-slate-700 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-700/40 border-b border-slate-700">
                        <th className="text-left text-slate-400 font-medium px-3 py-2">Booking</th>
                        <th className="text-left text-slate-400 font-medium px-3 py-2">Guest</th>
                        <th className="text-left text-slate-400 font-medium px-3 py-2">Applied By</th>
                        <th className="text-left text-slate-400 font-medium px-3 py-2">Date</th>
                        <th className="text-right text-slate-400 font-medium px-3 py-2">Discount</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {coupon.bookings.map(b => (
                        <tr key={b.bookingId} className="border-b border-slate-700/40 last:border-0 hover:bg-slate-700/20">
                          <td className="px-3 py-2">
                            <span className="font-mono text-slate-300">{b.bookingNumber}</span>
                          </td>
                          <td className="px-3 py-2 text-slate-300">{b.guestName}</td>
                          <td className="px-3 py-2">
                            <span className="flex items-center gap-1 text-slate-400">
                              <User className="h-3 w-3 shrink-0" />
                              {b.appliedBy}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-slate-400">{b.date}</td>
                          <td className="px-3 py-2 text-right text-green-400 font-medium">{fmtDiscount(b.discountAmount)}</td>
                          <td className="px-3 py-2 text-right">
                            <button
                              onClick={() => onViewBooking(b.bookingNumber)}
                              className="text-slate-500 hover:text-blue-400 transition-colors"
                              title="View booking"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Coupons() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = ['HotelHead', 'HotelAdmin', 'Admin'].includes(user?.role);

  const [activeTab, setActiveTab] = useState('codes');

  const [coupons, setCoupons]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [actionId, setActionId]   = useState(null);

  // Analytics
  const [analyticsPeriod, setAnalyticsPeriod] = useState('monthly');
  const [analytics, setAnalytics]             = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsLoaded, setAnalyticsLoaded]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setCoupons(await getCoupons()); }
    catch { toast.error('Failed to load coupons'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadAnalytics = useCallback(async (period) => {
    setAnalyticsLoading(true);
    try {
      setAnalytics(await getCouponAnalytics(period));
      setAnalyticsLoaded(true);
    } catch { toast.error('Failed to load analytics'); }
    finally { setAnalyticsLoading(false); }
  }, []);

  // Load analytics when switching to Usage tab
  useEffect(() => {
    if (activeTab === 'usage') loadAnalytics(analyticsPeriod);
  }, [activeTab, analyticsPeriod, loadAnalytics]);

  const handleViewBooking = (bookingNumber) => {
    navigate('/bookings', { state: { search: bookingNumber } });
  };

  const setField = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleCreate = async () => {
    if (!form.code.trim()) { toast.error('Coupon code is required'); return; }
    if (!form.discountValue || parseFloat(form.discountValue) <= 0) {
      toast.error('Discount value must be greater than 0'); return;
    }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        discountType: parseInt(form.discountType),
        discountValue: parseFloat(form.discountValue),
        minBookingAmount: form.minBookingAmount ? parseFloat(form.minBookingAmount) : null,
        maxUsageCount: form.maxUsageCount ? parseInt(form.maxUsageCount) : null,
        expiryDate: form.expiryDate || null,
      };
      await createCoupon(payload);
      toast.success('Coupon created');
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    setActionId(id);
    try {
      await toggleCoupon(id);
      setCoupons(prev => prev.map(c => c.id === id ? { ...c, isActive: !c.isActive } : c));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Delete coupon "${code}"? This cannot be undone.`)) return;
    setActionId(id);
    try {
      await deleteCoupon(id);
      setCoupons(prev => prev.filter(c => c.id !== id));
      toast.success('Coupon deleted');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActionId(null);
    }
  };

  const usageLabel = (c) =>
    c.maxUsageCount ? `${c.usageCount} / ${c.maxUsageCount}` : `${c.usageCount} used`;

  const discLabel = (c) =>
    c.discountType === 0 ? `${c.discountValue}%` : fmtMoney(c.discountValue);

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Tag className="h-6 w-6 text-blue-400" /> Discount Coupons
          </h1>
          <p className="text-slate-400 text-sm mt-1">Create and manage discount codes for guest bookings</p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'codes' && (
            <>
              <Button variant="outline" onClick={load} className="border-slate-600 text-slate-300 gap-2">
                <RefreshCw className="h-4 w-4" />
              </Button>
              {canManage && (
                <Button onClick={() => { setForm(EMPTY_FORM); setDialogOpen(true); }}
                  className="bg-blue-600 hover:bg-blue-700 gap-2">
                  <Plus className="h-4 w-4" /> New Coupon
                </Button>
              )}
            </>
          )}
          {activeTab === 'usage' && (
            <Button variant="outline" onClick={() => loadAnalytics(analyticsPeriod)} className="border-slate-600 text-slate-300 gap-2">
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-700 pb-0">
        {[
          { key: 'codes', label: 'Coupon Codes' },
          { key: 'usage', label: 'Usage Overview' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${
              activeTab === t.key
                ? 'text-white border-b-2 border-blue-500 -mb-px'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Coupon Codes Tab ── */}
      {activeTab === 'codes' && (loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : coupons.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-16 text-center">
            <Tag className="h-12 w-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No coupons yet. Create your first discount coupon.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-slate-800/50 border-slate-700">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left text-slate-400 font-medium px-4 py-3">Code</th>
                  <th className="text-left text-slate-400 font-medium px-4 py-3">Discount</th>
                  <th className="text-left text-slate-400 font-medium px-4 py-3">Min. Amount</th>
                  <th className="text-left text-slate-400 font-medium px-4 py-3">Usage</th>
                  <th className="text-left text-slate-400 font-medium px-4 py-3">Expiry</th>
                  <th className="text-left text-slate-400 font-medium px-4 py-3">Status</th>
                  {canManage && <th className="text-right text-slate-400 font-medium px-4 py-3">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {coupons.map(c => (
                  <tr key={c.id} className="border-b border-slate-700/50 hover:bg-slate-700/20 transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-mono font-bold text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded">
                        {c.code}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-semibold ${c.discountType === 0 ? 'text-green-400' : 'text-amber-400'}`}>
                        {discLabel(c)}
                      </span>
                      <span className="text-slate-500 text-xs ml-1">
                        {c.discountType === 0 ? 'off' : 'flat'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-300">
                      {c.minBookingAmount ? fmtMoney(c.minBookingAmount) : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-300">{usageLabel(c)}</td>
                    <td className="px-4 py-3 text-slate-300">{fmtDate(c.expiryDate)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        c.isActive
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}>
                        {c.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {canManage && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => handleToggle(c.id)}
                            disabled={actionId === c.id}
                            className={`h-8 w-8 ${c.isActive ? 'text-green-400 hover:text-red-400' : 'text-slate-500 hover:text-green-400'}`}
                            title={c.isActive ? 'Deactivate' : 'Activate'}
                          >
                            {actionId === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> :
                              c.isActive ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => handleDelete(c.id, c.code)}
                            disabled={actionId === c.id}
                            className="h-8 w-8 text-slate-500 hover:text-red-400"
                            title="Delete coupon"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      {/* ── Usage Overview Tab ── */}
      {activeTab === 'usage' && (
        <div className="space-y-4">
          {/* Period toggle */}
          <div className="flex items-center gap-2">
            {['monthly', 'weekly'].map(p => (
              <button
                key={p}
                onClick={() => setAnalyticsPeriod(p)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${
                  analyticsPeriod === p
                    ? 'bg-blue-600 border-blue-500 text-white'
                    : 'border-slate-600 text-slate-400 hover:border-slate-400 hover:text-slate-200'
                }`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>

          {analyticsLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : analytics.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-16 text-center">
                <Tag className="h-10 w-10 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No coupon usage found for this period.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {analytics.map(period => (
                <CouponPeriodCard
                  key={period.periodStart}
                  periodData={period}
                  onViewBooking={handleViewBooking}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Coupon Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Tag className="h-5 w-5 text-blue-400" /> Create Discount Coupon
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Code */}
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-sm">Coupon Code *</Label>
              <Input
                value={form.code}
                onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                placeholder="e.g. SUMMER25"
                className="bg-slate-700 border-slate-600 text-white font-mono uppercase"
              />
            </div>

            {/* Discount type + value */}
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-sm">Discount Type *</Label>
              <div className="flex gap-2">
                <button
                  onClick={() => setForm(p => ({ ...p, discountType: 0 }))}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${
                    form.discountType === 0
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : 'border-slate-600 text-slate-400 hover:border-slate-500'
                  }`}
                >
                  Percentage (%)
                </button>
                <button
                  onClick={() => setForm(p => ({ ...p, discountType: 1 }))}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${
                    form.discountType === 1
                      ? 'bg-amber-600 border-amber-500 text-white'
                      : 'border-slate-600 text-slate-400 hover:border-slate-500'
                  }`}
                >
                  Flat Amount (৳)
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-slate-300 text-sm">
                Discount Value * {form.discountType === 0 ? '(%)' : '(৳)'}
              </Label>
              <Input
                type="number" min="0.01"
                max={form.discountType === 0 ? 100 : undefined}
                step="0.01"
                value={form.discountValue}
                onChange={setField('discountValue')}
                placeholder={form.discountType === 0 ? 'e.g. 15' : 'e.g. 500'}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>

            {/* Optional fields */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">Min. Booking Total (৳)</Label>
                <Input
                  type="number" min="0" value={form.minBookingAmount}
                  onChange={setField('minBookingAmount')}
                  placeholder="No minimum"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">Max Uses</Label>
                <Input
                  type="number" min="1" value={form.maxUsageCount}
                  onChange={setField('maxUsageCount')}
                  placeholder="Unlimited"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-slate-300 text-sm">Expiry Date</Label>
              <Input
                type="date" value={form.expiryDate}
                min={new Date().toISOString().split('T')[0]}
                onChange={setField('expiryDate')}
                className="bg-slate-700 border-slate-600 text-white"
              />
              <p className="text-slate-500 text-xs">Leave blank for no expiry</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}
              className="border-slate-600 text-slate-300">Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Coupon
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
